import React from "react";
import { Box, Grid, Tooltip } from "@mui/material";
import { Label12Regular } from "components";
import { useGetConfigParameter } from "hooks";
import { combineDropdownOptions, isFeatureEnabled, toUSNumber } from "utils";
import { additionalColorStatuses, approvalFlowColorStatuses, basicColorStatuses } from "../consts";
import { DataEntriesStatusType, StatusDataApiType, StatusType } from "../types";

const ProgressBar = ({ data }: { data: DataEntriesStatusType }) => {
  const { data: configParameter } = useGetConfigParameter();
  const statusDataFromApi = data?.billStatusCount || [];

  const statusDataParsed = statusDataFromApi?.reduce(
    (acc: StatusDataApiType[], { status, count, percentage }: StatusDataApiType) => {
      const actualStatus = status.includes("Not Applicable")
        ? "Not Applicable"
        : status;

      const existing = acc.find((item) => item.status === actualStatus);

      if (existing) {
        existing.count += count;
        existing.percentage += percentage;
      } else {
        acc.push({ status: actualStatus, count, percentage });
      }
      return acc;
    },
    []
  );

  const isApprovalFlowValueOn = isFeatureEnabled(configParameter, "second_layer_approval_feature");
  const isAdditionalStatusesOn = isFeatureEnabled(configParameter, "unusable_status_enabled");

  // Right below 👇 we have basicColorStatuses always on, that means = true;
  const currentStatuses: Array<StatusType> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [basicColorStatuses, approvalFlowColorStatuses, additionalColorStatuses],
  );

  const optionsToRender = currentStatuses?.map(({ label, color }: StatusType) => {
    const apiEntry = statusDataParsed?.find(({ status }: StatusDataApiType) => status === label);
    return {
      status: label,
      count: apiEntry?.count || 0,
      percentage: apiEntry?.percentage || 0,
      color,
    };
  });

  return (
    <Box
      sx={{
        width: "350px",
        maxWidth: 350,
        padding: 2,
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
      }}
    >
      <Grid container spacing={1}>
        {optionsToRender?.map((status) => {
          const { status: statusLabel, count, percentage, color } = status;

          return (
            <Grid item xs={12} key={`status-${statusLabel}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
                {/* Status Label */}
                <Label12Regular
                  sx={{
                    minWidth: "133px",
                    flexShrink: 0,
                    color: "#000000",
                    fontWeight: 500
                  }}>
                  {statusLabel}
                </Label12Regular>
                {/* Progress Bar */}
                <Tooltip
                  title={`${toUSNumber(count) || 0} Data ${count === 1 ? "Entry" : "Entries"}`}
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      height: 14,
                      flexGrow: 1,
                      backgroundColor: "#EAEAEA",
                      borderRadius: 1,
                      position: "relative",
                      minWidth: "20px",
                      maxWidth: "90%"
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        backgroundColor: color,
                        width: `${percentage}%`,
                        borderRadius: 1,
                        marginLeft: 0,
                        minWidth: "0px",
                      }}
                    />
                  </Box>
                </Tooltip>
                {/* Percentage */}
                <span style={{ minWidth: "30px", color: "#000000", fontSize: "10px", fontWeight: 600, fontStyle: "Inter", textAlign: "left" }}>
                  {percentage}%
                </span>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  );
};

export default ProgressBar;
